import React, { FC, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from 'ui/Button';
import Input from 'ui/Input/index';
import CompanyAutocomplete from 'ui/CompanyAutocomplete/index';
import SelectCountry from 'ui/SelectCountry/index';
import SelectCity from 'ui/SelectCity/index';
import Select from 'ui/Select';
import Textarea from 'ui/Textarea/index';
import DragAndDrop from 'ui/DragAndDrop';
import File from 'ui/File';
import Preloader from 'ui/Preloader/Preloader';
import { useValidation } from 'utils/hooks/useValidation';
import { useUtils } from 'utils/useUtils';
import { useBlacklistRequests } from 'api/requests';
import { BlacklistActions } from 'redux/actionCreators/blacklistActions';
import { ICompanyErrors, ICity, IFormData, ICompany } from './interfaces';
import { debounce } from 'utils/helper';

interface IForm {
  isFormVisible: boolean;
  setIsFormVisible: (state: boolean) => void;
}

const Form: FC<IForm> = ({ isFormVisible, setIsFormVisible }: IForm) => {
  const dispatch = useDispatch();
  const { countries, reasons, managers, companyToEdit } = useSelector((state: any) => state.blacklistReducer);
  const { BlacklistUtils } = useUtils();
  const { getOneCompanyRequest, getCities, addNewCompany, editCompany, removeFile } = useBlacklistRequests();
  const {
    isNameValid,
    isWebsiteValid,
    isUserNameValid,
    isEmailValid,
    isEmailNotBlacklisted,
    isPhoneNumberValid,
    isCountryValid,
    isDateOfIncidentValid,
    isReasonValid,
    isManagerValid,
    isTextareaValid,
    isFilesValid,
  } = useValidation();

  const [cities, setCities] = useState<ICity[]>([]);
  const [formData, setFormData] = useState<IFormData>({
    id: '',
    company_name: '',
    country_code: '',
    country_name: '',
    email: '',
    website: '',
    logo: '',
    phone: '',
    city_name: '',
    key_person: '',
    date_of_incident: '',
    reason: '',
    manager: '',
    description: '',
    doc_files: [],
    logo_file: '',
  });
  const [formErrors, setFormErrors] = useState<ICompanyErrors>({
    id: '',
    company_name: '',
    country_code: '',
    country_name: '',
    email: '',
    website: '',
    logo: '',
    phone: '',
    city_name: '',
    key_person: '',
    date_of_incident: '',
    reason: '',
    manager: '',
    description: '',
    files: '',
  });
  const [searchedCompanies, setSearchedCompanies] = useState<ICompany[]>([]);
  const [searchCountryValue, setSearchCountryValue] = useState<string>('');
  const [searchedCountries, setSearchedCountries] = useState<any[]>([]);
  const [searchCityValue, setSearchCityValue] = useState<string>('');
  const [searchedCities, setSearchedCities] = useState<any[]>([]);
  // const [ selectedCountry, setSelectedCountry ] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCompaniesLoading, setIsCompaniesLoading] = useState<boolean>(false);
  const [isFormDataCleared, setIsFormDataCleared] = useState<boolean>(false);

  const isFormDataValid = () => {
    const companyNameValidator = isNameValid({ formData, key: 'company_name', setFormErrors });
    const websiteNameValidator = isWebsiteValid({ formData, key: 'website', setFormErrors });
    const userNameValidator = isUserNameValid({ formData, key: 'key_person', setFormErrors });
    const emailValidator = isEmailValid({ formData, key: 'email', setFormErrors });
    const emailBlacklistValidator = isEmailNotBlacklisted({ formData, key: 'email', setFormErrors });
    const phoneNumberValidator = isPhoneNumberValid({ formData, key: 'phone', setFormErrors });
    const countryValidator = isCountryValid({ formData, key: 'country_name', setFormErrors });
    const cityValidator = isCountryValid({ formData, key: 'city_name', setFormErrors });
    const dateOfIncidentValidator = isDateOfIncidentValid({ formData, key: 'date_of_incident', setFormErrors });
    const reasonValidator = isReasonValid({ formData, key: 'reason', setFormErrors });
    const managerValidator = isManagerValid({ formData, key: 'manager', setFormErrors });
    const descriptionValidator = isTextareaValid({ formData, key: 'description', setFormErrors });
    const filesValidator = isFilesValid({ formData, key: 'doc_files', setFormErrors });

    return (
      companyNameValidator &&
      websiteNameValidator &&
      userNameValidator &&
      emailValidator &&
      emailBlacklistValidator &&
      phoneNumberValidator &&
      countryValidator &&
      dateOfIncidentValidator &&
      cityValidator &&
      reasonValidator &&
      managerValidator &&
      descriptionValidator &&
      filesValidator
    );
  };

  const saveHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!isFormDataValid()) return;

    if (companyToEdit.length) {
      const sendFormData = async () => {
        // const newFormData = new FormData();

        // Object.keys(formData).forEach((item: any) => {
        //   //@ts-ignore
        //   if (Array.isArray(formData[item])) {
        //     //@ts-ignore
        //     formData[item].forEach((value: any) => {
        //       newFormData.append(item + '[]', value);
        //     });
        //     return;
        //   }
        //   //@ts-ignore
        //   newFormData.append(item, formData[item]);
        // });
        return await editCompany(formData);
      };
      setIsLoading(true);
      const { data: response } = await sendFormData();

      setIsLoading(false);
      dispatch(BlacklistActions.editCompany(response.data));
      dispatch(BlacklistActions.setCompanyToEdit(0));
      setFormData(BlacklistUtils.clearFormData(formData));
      setIsFormDataCleared(!isFormDataCleared);
      setIsFormVisible(false);
    } else {
      const data = {
        id: formData.id,
        company_name: formData.company_name,
        country_code: formData.country_code,
        country_name: formData.country_name,
        email: formData.email,
        website: formData.website,
        phone: formData.phone,
        city_name: formData.city_name,
        key_person: formData.key_person,
        date_of_incident: formData.date_of_incident,
        reason: formData.reason,
        manager: formData.manager,
        description: formData.description,
      };

      try {
        setIsLoading(true);
        const { data: response, status } = await addNewCompany(data);
        setIsLoading(false);

        if (status === 200) {
          const sendFormData = async () => {
            return await editCompany({ ...formData, id: response.data.id });
          };
          setIsLoading(true);
          const { data: filesUploadResponse } = await sendFormData();

          setIsLoading(false);
          dispatch(BlacklistActions.setOneCompany(filesUploadResponse.data));
          setFormData(BlacklistUtils.clearFormData(formData));
          setIsFormDataCleared(!isFormDataCleared);
          setIsFormVisible(false);
        }
      } catch (error: any) {
        console.log('saveHandler - error: ', error.response.data);
      }
    }
  };

  const cancelHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setFormData(BlacklistUtils.clearFormData(formData));
    setIsFormDataCleared(!isFormDataCleared);
    dispatch(BlacklistActions.setCompanyToEdit(0));
    setIsFormVisible(false);
  };

  const removeFileHandler = async (fileId: number, fileName: string, companyId: number) => {
    try {
      setIsLoading(true);
      const response = await removeFile(fileId);
      setIsLoading(false);
      if (response.status === 200) {
        dispatch(BlacklistActions.removeFile(fileId, fileName, companyId));
      }
    } catch (error: any) {
      console.log('Form - removeHandler - error: ', error.data);
    }
  };

  const fileElements = companyToEdit[0]?.files?.map((item: any, index: number) => {
    return (
      <li className="blacklist__files-list-item" key={`${item.id}_${index}`}>
        <div className="blacklist__files-list-item-section">
          <div className={`blacklist__file-icon icon-${index}`} />
          <span className="blacklist__file-name">{item.filename ?? item.name}</span>
        </div>
        <div className="blacklist__files-list-item-section">
          <img
            className="blacklist__file-remove"
            src="/images/pages/Resources/Blacklist/card-item_icon-remove.svg"
            alt="remove-file"
            onClick={() => removeFileHandler(item.id, item.name, companyToEdit[0].id)}
          />
        </div>
      </li>
    );
  });

  useEffect(() => {
    if (searchCountryValue === '') {
      setSearchedCountries([]);
    } else {
      setSearchedCountries(BlacklistUtils.searchCountries(countries, searchCountryValue));
    }
  }, [searchCountryValue]);

  useEffect(() => {
    if (searchCityValue === '') {
      setSearchedCities([]);
    } else {
      setSearchedCities(BlacklistUtils.searchCities(cities, searchCityValue));
    }
  }, [searchCityValue]);

  useEffect(() => {
    if (companyToEdit.length) {
      setFormData((state: any) => {
        return {
          ...state,
          id: companyToEdit[0].id,
          company_name: companyToEdit[0].company_name,
          country_code: companyToEdit[0].country_code,
          country_name: companyToEdit[0].country_name,
          email: companyToEdit[0].email,
          website: companyToEdit[0].website,
          logo: companyToEdit[0].logo,
          phone: companyToEdit[0].phone,
          city_name: companyToEdit[0].city_name,
          key_person: companyToEdit[0].key_person,
          date_of_incident: companyToEdit[0].date_of_incident,
          reason: companyToEdit[0].reason,
          manager: companyToEdit[0].manager,
          description: companyToEdit[0].description,
        };
      });

      setIsFormVisible(true);
    } else {
      return;
    }
  }, [companyToEdit]);

  const fetchCompanies = async (searchCompanyValue: string) => {
    if (searchCompanyValue === '') {
      setSearchedCompanies([]);
      return;
    }
    try {
      setIsCompaniesLoading(true);
      const { data: response } = await getOneCompanyRequest(searchCompanyValue);
      setSearchedCompanies(response.data);
      setIsCompaniesLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const searchCompany = debounce(fetchCompanies, 1000);

  const fetchCities = async (countryCode: string) => {
    try {
      const { data: response } = await getCities(countryCode);
      setCities(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <form className={`blacklist__form ${isFormVisible ? 'is-visible' : ''}`} autoComplete="off">
      <div className="blacklist__form-section">
        <div className="blacklist__form-fields-box">
          <div className="blacklist__form-fields">
            <CompanyAutocomplete
              id="company-autocomplete"
              name="company_name"
              label="Company"
              placeholder="Select"
              dropdownItems={searchedCompanies}
              value={formData.company_name}
              error={formErrors.company_name}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              setSearchCompanyValue={searchCompany}
              readOnly={false}
              isCompaniesLoading={isCompaniesLoading}
            />
            <Input
              id="input-website"
              name="website"
              label="Website"
              placeholder="www.website.com"
              value={formData.website}
              error={formErrors.website}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
            />
            <Input
              id="input-key-person"
              name="key_person"
              label="Key person"
              placeholder="John Doe"
              value={formData.key_person}
              error={formErrors.key_person}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
            />
            <SelectCountry
              id="select-country"
              name="country_name"
              label="Country"
              placeholder="Select"
              dropdownItems={searchedCountries}
              countries={countries}
              value={formData.country_name}
              error={formErrors.country_name}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              searchCountryValue={searchCountryValue}
              setSearchCountryValue={setSearchCountryValue}
              fetchCities={fetchCities}
              // setSelectedCountry={setSelectedCountry}
              readOnly={false}
            />
          </div>
          <div className="blacklist__form-fields">
            <Input
              id="input-email"
              name="email"
              label="Email"
              placeholder="enterCorporate@email.com"
              value={formData.email}
              error={formErrors.email}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
            />
            <Input
              id="input-phone"
              name="phone"
              label="Phone"
              placeholder="+380631112233"
              value={formData.phone}
              error={formErrors.phone}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
            />
            <Input
              id="input-incident"
              type="date"
              name="date_of_incident"
              label="Date of incident"
              placeholder="YYYY-MM-DD"
              value={formData.date_of_incident}
              error={formErrors.date_of_incident}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
            />
            <SelectCity
              id="select-city"
              name="city_name"
              label="City"
              placeholder="Select"
              dropdownItems={searchedCities}
              cities={cities}
              value={formData.city_name}
              countryCode={formData.country_code}
              error={formErrors.city_name}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              searchCityValue={searchCityValue}
              setSearchCityValue={setSearchCityValue}
              readOnly={false}
            />
          </div>
          <div className="blacklist__form-fields">
            <Select
              id="reason"
              name="reason"
              label="Reason"
              placeholder="Select"
              value={formData.reason}
              error={formErrors.reason}
              dropdownItems={reasons}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              readOnly={true}
            />
            <Select
              id="manager"
              name="manager"
              label="Manager"
              placeholder="Select"
              value={formData.manager}
              error={formErrors.manager}
              dropdownItems={managers}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              readOnly={true}
            />
            <Textarea
              id="description"
              name="description"
              label="Description"
              placeholder="Description"
              value={formData.description}
              error={formErrors.description}
              setFormData={setFormData}
              setFormErrors={setFormErrors}
              customStyle={{
                height: '114px',
              }}
            />
          </div>
        </div>
        <div>
          <DragAndDrop
            id="file"
            name="doc_files"
            label="Choose document"
            error={formErrors.files}
            formData={formData}
            setFormData={setFormData}
            setFormErrors={setFormErrors}
            isFormDataCleared={isFormDataCleared}
          />
          <File
            id="logo"
            name="logo_file"
            label="Choose logo"
            fileName={formData.logo_file ?? ''}
            error={formErrors.logo}
            formData={formData}
            setFormData={setFormData}
            setFormErrors={setFormErrors}
            isFormDataCleared={isFormDataCleared}
          />
        </div>
      </div>

      <div className="blacklist__form-section">
        <ul className="blacklist__files-list">{fileElements}</ul>

        <div className="blacklist__form-actions">
          {isLoading ? (
            <Preloader />
          ) : (
            <Button
              name="button-add"
              action={saveHandler}
              stylePreset="dark"
              customStyle={{
                width: '150px',
                marginRight: '8px',
              }}
            >
              <span className="blacklist__filter-button-label" style={{ color: '#ffffff' }}>
                Save
              </span>
            </Button>
          )}
          <Button
            name="button-add"
            action={cancelHandler}
            customStyle={{
              width: '92px',
              backgroundColor: '#EFF1F6',
            }}
          >
            <span className="blacklist__filter-button-label" style={{ color: '#000c37' }}>
              Cancel
            </span>
          </Button>
        </div>
      </div>
    </form>
  );
};

export default React.memo(Form);
